import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Button, SxProps, Theme, Typography } from '@mui/material';
import React from 'react';

import { DEFAULT_ERROR_MESSAGE } from '../../../constants/errors';
import {
  CLEAR_FILTERS_LABEL,
  FULL_BUDGET_BODY,
  FULL_BUDGET_BODY_MAX_WIDTH,
  FULL_BUDGET_TITLE,
  getNothingElseFitsTitle,
  NOTHING_ELSE_FITS_BODY,
  NOTHING_FITS_TEXT,
  NO_RESULTS_TEXT,
  SHOW_ALL_CATEGORIES_LABEL,
  SHOW_ALL_ITEMS_LABEL,
} from '../../../constants/marketplace';
import { IError } from '../../../types/store';

interface IProps {
  error: IError | null;
  hasSpentCredits: boolean;
  categoryName: string;
  hasSearch: boolean;
  hasPicksToReturnTo: boolean;
  onRetry: () => void;
  onOpenCart: () => void;
  onShowAllItems: () => void;
  onShowAllCategories: () => void;
  onClearFilters: () => void;
  onBackToPicks: () => void;
}

const emptyStateSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1,
  py: 6,
  px: 2,
  textAlign: 'center',
};

const emptyStateActionsSx: SxProps<Theme> = {
  mt: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap',
  justifyContent: 'center',
};

const fullBudgetIconSx: SxProps<Theme> = { fontSize: 40, color: 'primary.main' };

const fullBudgetBodySx: SxProps<Theme> = { maxWidth: FULL_BUDGET_BODY_MAX_WIDTH };

export const MarketplaceEmptyState: React.FC<IProps> = ({
  error,
  hasSpentCredits,
  categoryName,
  hasSearch,
  hasPicksToReturnTo,
  onRetry,
  onOpenCart,
  onShowAllItems,
  onShowAllCategories,
  onClearFilters,
  onBackToPicks,
}) => {
  if (error) {
    return (
      <Box sx={emptyStateSx}>
        <Typography variant="body1" color="text.secondary">
          {error.message || DEFAULT_ERROR_MESSAGE}
        </Typography>
        <Button variant="text" color="primary" onClick={onRetry}>
          Retry
        </Button>
      </Box>
    );
  }

  const isNarrowed = !!categoryName || hasSearch;

  if (hasSpentCredits) {
    return (
      <Box sx={emptyStateSx}>
        {!isNarrowed && <CheckCircleOutlineIcon sx={fullBudgetIconSx} />}
        <Typography variant="subtitle1" fontWeight="medium">
          {isNarrowed ? getNothingElseFitsTitle(categoryName) : FULL_BUDGET_TITLE}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={fullBudgetBodySx}>
          {isNarrowed ? NOTHING_ELSE_FITS_BODY : FULL_BUDGET_BODY}
        </Typography>
        <Box sx={emptyStateActionsSx}>
          {isNarrowed && (
            <Button variant="outlined" color="primary" onClick={onShowAllCategories}>
              {SHOW_ALL_CATEGORIES_LABEL}
            </Button>
          )}
          <Button variant="outlined" color="primary" aria-haspopup="dialog" onClick={onOpenCart}>
            View picks
          </Button>
          <Button variant="text" color="primary" onClick={onShowAllItems}>
            {SHOW_ALL_ITEMS_LABEL}
          </Button>
        </Box>
      </Box>
    );
  }

  if (isNarrowed) {
    return (
      <Box sx={emptyStateSx}>
        <Typography variant="body1" color="text.secondary">
          {NO_RESULTS_TEXT}
        </Typography>
        <Button variant="text" color="primary" onClick={onClearFilters}>
          {CLEAR_FILTERS_LABEL}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={emptyStateSx}>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        {NOTHING_FITS_TEXT}
      </Typography>
      {hasPicksToReturnTo && (
        <Button variant="outlined" color="primary" startIcon={<ArrowBackIcon />} onClick={onBackToPicks}>
          Back to your options
        </Button>
      )}
    </Box>
  );
};
